import { formatToTimeZone } from '@zenchef/date-fns-timezone'
import { Skeleton, Text } from '@zenchef/ds-react'
import { format, isSameMonth } from 'date-fns'
import { observer } from 'mobx-react-lite'
import { useContext, useMemo } from 'react'

import SelectableItem from '@/components/redesign/common/SelectableItem'
import getDateFnsLocal from '@/utils/getDateFnsLocal'
import { useTranslation } from '@/utils/hooks'
import useDayModifiers from '@/utils/hooks/useDayModifiers'
import StoresContext from '@/utils/StoresContext'
import { getRelativeDateFormat, getShortDateFormat, getShorterDateFormat } from '@/utils/useFormattedDateAndTime'

interface DayButtonProps {
  day?: string
  isLoading?: boolean
  handleDaySelected: (day: string) => void
  handleClose: () => void
}

const DayButton = observer(({ day, isLoading, handleDaySelected, handleClose }: DayButtonProps) => {
  const { appStore } = useContext(StoresContext)
  const { t } = useTranslation()

  const { modifiersFunctions } = useDayModifiers({ convertDateToUTC: false })

  const locale = getDateFnsLocal(appStore.state.language)
  const today = formatToTimeZone(new Date(), 'YYYY-MM-DD', { timeZone: 'UTC' })

  const isThisMonth = day ? isSameMonth(day, today) : false
  const isOpen = day ? modifiersFunctions.open(day) : false
  const isWaitlistOpen = day ? modifiersFunctions.waitlistOpen(day) : false
  const isPast = day ? modifiersFunctions.past(day) : false

  const relativeDateLabel = useMemo(() => {
    if (!day || !appStore.state.restaurantTimezone) return null

    return t(...getRelativeDateFormat(day, appStore.state.restaurantTimezone))
  }, [day, appStore.state.restaurantTimezone, t])

  const dateName = day
    ? format(
        day,
        isThisMonth ? getShorterDateFormat(appStore.state.language) : getShortDateFormat(appStore.state.language),
        {
          locale
        }
      )
    : null

  const onSelect = async () => {
    if (isPast) {
      return
    }
    if (day && appStore.state.wish.day !== day) {
      await appStore.selectWishDay(day)
      handleDaySelected(day)
    }

    if (isOpen || isWaitlistOpen) {
      handleClose()
    }
  }

  return (
    <Skeleton height='100%' borderRadius='l' isLoaded={!isLoading} reversed flex='1'>
      <SelectableItem
        horizontal
        disabled={!isOpen && !isWaitlistOpen}
        onClick={onSelect}
        isWaitlist={isWaitlistOpen}
        selected={day ? appStore.state.wish.day === day : false}
        testId={`day-${day}`}
        p='padding.3'>
        <Text textStyle='paragraph.m.bold'>{dateName}</Text>
        <Text textStyle='paragraph.m.regular'>{relativeDateLabel}</Text>
      </SelectableItem>
    </Skeleton>
  )
})

DayButton.displayName = 'DayButton'

export default DayButton
